import React, { useEffect, useState } from "react";
import { addParty, getSingleParty } from "../../services/services";
import { toastError, toastSuccess } from "../../utils";
import moment from "moment";
import DatePicker from "react-datepicker";
import CalendarInput from "../Modal/CalendarInput";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import DateInput from "../DateInput";

const AddPartyModal = ({ id, isOpen, onClose, onPartyAdded, context }) => {
  const isEdit = !!id; // true if editing, false if adding new
  const [party, setParty] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const indianStates = [
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chhattisgarh",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
    "Delhi",
    "Puducherry",
    "Lakshadweep",
    "Jammu and Kashmir",
    "Ladakh",
  ];

  const gstTypes = ["Registered", "Unregistered", "Composition", "Consumer"];

  const initialFormData = {
    _id: "",
    name: "",
    mobile: "",
    email: "",
    gstin: "",
    billingAddress: "",
    shippingAddress: "",
    state: "Madhya Pradesh",
    gstType: "Unregistered",
    partyType: context === "purchase" ? "vendor" : "customer",
    openingBalance: 0,
    openingBalanceType: "toReceive",
    date: moment().format("YYYY-MM-DD"),
  };

  const [formData, setFormData] = useState(initialFormData);
  const partyTypeOptions =
    context === "sale"
      ? ["customer", "both"]
      : context === "purchase"
        ? ["vendor", "both"]
        : ["customer", "vendor", "both"];

  // Reset form when modal opens/closes or id changes
  useEffect(() => {
    if (!isOpen) {
      setFormData(initialFormData);
      setErrors({});
      setParty(null);
      return;
    }

    if (isEdit && id) {
      fetchParty();
    } else {
      setFormData(initialFormData);
      setErrors({});
    }
  }, [isOpen, id, isEdit]);

  // Load party data when editing
  const fetchParty = async () => {
    setLoading(true);
    try {
      const response = await getSingleParty(id);
      const p = response.data.party;

      const absBalance = Math.abs(p.openingBalance || 0);
      setFormData({
        _id: p._id,
        name: p.name || "",
        mobile: p.mobile || "",
        email: p.email || "",
        gstin: p.gstin || "",
        billingAddress: p.billingAddress || "",
        shippingAddress: p.shippingAddress || "",
        state: p.state || "Madhya Pradesh",
        gstType: p.gstType || "Unregistered",
        partyType: p.partyType || "customer",
        openingBalance: absBalance,
        openingBalanceType: p.openingBalance >= 0 ? "toReceive" : "toPay",
        date: p.date
          ? moment(p.date).format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD"),
      });
    } catch (error) {
      toastError(
        error.response?.data?.message || "Failed to load party details",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "openingBalance"
          ? value === ""
            ? 0
            : parseFloat(value) || 0
          : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({ ...prev, mobile: value }));
    setErrors((prev) => ({ ...prev, mobile: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Party name is required";
    if (formData.mobile && !/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = "Mobile must be exactly 10 digits";
    }
    if (formData.gstin.trim()) {
      if (formData.gstin.length !== 15) {
        newErrors.gstin =
          "GSTIN must be exactly 15 characters (22AAAAA0000A1Z5)";
      } else if (
        !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(formData.gstin)
      ) {
        newErrors.gstin = "Invalid GSTIN format (Example: 22AAAAA0000A1Z5)";
      }
    }
    if (!formData.state) newErrors.state = "State is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await addParty(formData);
      if (response.data.status) {
        toast.success(
          response.data.message ||
            (isEdit
              ? "Party updated successfully"
              : "Party added successfully"),
        );
        onPartyAdded(response.data.response);
        onClose();
      }
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to save party";
      toastError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      tabIndex="-1"
      role="dialog"
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        role="document"
      >
        <div className="modal-content">
          <div className="modal-header bg-custom text-white">
            <h5 className="modal-title">
              {isEdit ? "Update Party" : "Add New Party"}
            </h5>
            <X className="cursor-pointer" size={24} onClick={onClose} />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row">
                {/* Party Name */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Party Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`form-control ${
                      errors.name ? "is-invalid" : ""
                    }`}
                    placeholder="Enter party name"
                    required
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>

                {/* Party Type */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Party Type</label>
                  <select
                    name="partyType"
                    value={formData.partyType}
                    onChange={handleChange}
                    className="form-select"
                  >
                    {partyTypeOptions.map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Mobile */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Mobile</label>
                  <input
                    type="text"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleMobileChange}
                    maxLength={10}
                    className={`form-control ${
                      errors.mobile ? "is-invalid" : ""
                    }`}
                    placeholder="10-digit mobile number"
                  />
                  {errors.mobile && (
                    <div className="invalid-feedback">{errors.mobile}</div>
                  )}
                </div>

                {/* GSTIN */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">GSTIN</label>
                  <input
                    type="text"
                    name="gstin"
                    value={formData.gstin}
                    maxLength={15}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        gstin: e.target.value.toUpperCase(),
                      }))
                    }
                    className={`form-control ${errors.gstin ? "is-invalid" : ""}`}
                    placeholder="e.g. 22AAAAA0000A1Z5"
                  />
                  {errors.gstin && (
                    <div className="invalid-feedback">{errors.gstin}</div>
                  )}
                </div>

                {/* Opening Balance + Type */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Opening Balance</label>
                  <input
                    type="number"
                    name="openingBalance"
                    value={formData.openingBalance}
                    onChange={handleChange}
                    className="form-control"
                    min="0"
                    step="0.01"
                  />
                  <div className="mt-2 d-flex gap-4">
                    <label className="form-check">
                      <input
                        type="radio"
                        name="openingBalanceType"
                        value="toReceive"
                        checked={formData.openingBalanceType === "toReceive"}
                        onChange={handleChange}
                        className="form-check-input"
                      />
                      <span className="form-check-label">To Receive</span>
                    </label>
                    <label className="form-check">
                      <input
                        type="radio"
                        name="openingBalanceType"
                        value="toPay"
                        checked={formData.openingBalanceType === "toPay"}
                        onChange={handleChange}
                        className="form-check-input"
                      />
                      <span className="form-check-label">To Pay</span>
                    </label>
                  </div>
                </div>

                {/* Date */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Date</label>
                  <DateInput
                    value={formData.date}
                    onChange={(newDate) =>
                      setFormData((prev) => ({
                        ...prev,
                        date: newDate,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="row">
                {/* Left Column */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Enter email address"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Billing Address</label>
                    <textarea
                      name="billingAddress"
                      value={formData.billingAddress}
                      onChange={handleChange}
                      className="form-control"
                      rows="2"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Shipping Address</label>
                    <textarea
                      name="shippingAddress"
                      value={formData.shippingAddress}
                      onChange={handleChange}
                      className="form-control"
                      rows="2"
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">GST Type</label>
                    <select
                      name="gstType"
                      value={formData.gstType}
                      onChange={handleChange}
                      className="form-select"
                    >
                      {gstTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">State</label>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className="form-select"
                    >
                      {indianStates.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? "Saving..." : isEdit ? "Update Party" : "Add Party"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddPartyModal;
